import {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import {
    useAnalyticsEvents,
    CreateUIAnalyticsEvent
} from "@atlaskit/analytics-next";

import {
    getSocialButtonUrl,
    ufoEventAccountSignin,
    getIdentityBaseUrl,
    getMethodsAvailableRedirection,
    getRecaptcha
} from "@/components/signup/utils";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/index";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { setLocalStorage } from "@/utils/localstorage";
import { useLocation } from "@/utils/location-provider";

function trackFormSubmit(createAnalyticsEvent: CreateUIAnalyticsEvent) {
    createAnalyticsEvent({
        eventName: "gwp.formCloudSignIn.submitWithCaptcha",
        eventType: "ui",
        action: "clicked",
        actionSubject: "linkButton",
        actionSubjectId: "GenearlSigninPromptSubmitWithCaptcha"
    }).fire();
}

function trackContinueWithSocialSignin(
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    type: string
) {
    createAnalyticsEvent({
        eventName: "gwp.formCloudSignIn.socialButtonClick",
        eventType: "ui",
        action: "clicked",
        actionSubject: "linkButton",
        actionSubjectId: "GenearlSigninPromptSocialButtonClick",
        attributes: {
            socialProvider: type
        }
    }).fire();
}

export interface AccountSigninContext {
    formError: boolean;
    isReadyForInvalid: boolean;
    isSubmitting: boolean;
    onClickSocialButton: (provider: string) => void;
    onClickSubmitButton: () => void;
    onSubmitWithReCAPTCHA: ({ email }: { email: string }) => Promise<void>;
    redirectTo: string;
    setIsEmailValid: Dispatch<SetStateAction<boolean>>;
    setIsReadyForInvalid: Dispatch<SetStateAction<boolean>>;
    setTypeAheadEmail: Dispatch<SetStateAction<string>>;
    typeAheadEmail: string;
}

export const AccountSigninContext = createContext<AccountSigninContext | null>(
    null
);

export const useAccountSigninContext = (): AccountSigninContext => {
    const context = useContext(AccountSigninContext);
    if (!context) {
        throw new Error(
            "useAccountSigninContext must be used within an AccountSigninContextProvider"
        );
    }
    return context;
};

export const AccountSigninContextProvider = ({
    children
}: PropsWithChildren) => {
    const location = useLocation();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isReadyForInvalid, setIsReadyForInvalid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(false);
    const [, setEmail] = useState("");
    const [typeAheadEmail, setTypeAheadEmail] = useState("");
    const [onSubmitProps, setOnSubmitProps] = useState<
        { email: string } | undefined
    >(undefined);
    const [recaptchaFailedToLoad, setRecaptchaFailedToLoad] =
        useState<boolean>(false);
    const [
        recaptchaRetryTimeRemainingSecond,
        setRecaptchaRetryTimeRemainingSecond
    ] = useState(60);

    const signupClient = useBxpWebStargateClient();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const onSubmitWithReCAPTCHA = useCallback(
        async ({ email }: { readonly email: string }) => {
            setIsReadyForInvalid(true);
            if (!email || !isEmailValid) {
                createAnalyticsEvent({
                    eventName: "gwp.formCloudSignIn.submitFailureInvalidEmail",
                    eventType: "ui",
                    action: "submitted",
                    actionSubject: "modal",
                    actionSubjectId:
                        "GeneralSigninPromptSubmitFailureInvalidEmail",
                    attributes: {
                        event: "submitFailed"
                    }
                }).fire();
                return;
            }
            setEmail(email);
            setOnSubmitProps({ email });
            setIsSubmitting(true);
            if (!executeRecaptcha && recaptchaRetryTimeRemainingSecond > 0) {
                // set trigger that will wait for Recaptcha retry logic
                setFormError(true);
                setIsSubmitting(false);
                setRecaptchaFailedToLoad(true);
                return;
            }
            setRecaptchaFailedToLoad(false);

            const gRecaptchaResponse = await getRecaptcha(
                email,
                executeRecaptcha
            );

            ufoEventAccountSignin.setCustomData({
                gRecaptchaResponse: gRecaptchaResponse ?? "none",
                executeRecaptchaExists: Boolean(executeRecaptcha),
                reactComponent: "AccountSigninContextProvider"
            });

            // Handle missing reCAPTCHA response
            if (!gRecaptchaResponse) {
                createAnalyticsEvent({
                    eventName: "gwp.formCloudSignIn.submitFailureRecaptcha",
                    eventType: "ui",
                    action: "submitted",
                    actionSubject: "modal",
                    actionSubjectId:
                        "GeneralSigninPromptSubmitFailureRecaptcha",
                    attributes: {
                        event: "submitFailed"
                    }
                }).fire();

                const invalidRecaptchaError = new Error(
                    "Missing reCAPTCHA token"
                );

                ufoEventAccountSignin.failure({
                    customData: {
                        reactComponent: "AccountSigninContextProvider"
                    },
                    error: invalidRecaptchaError
                });
                throw invalidRecaptchaError;
            }

            trackFormSubmit(createAnalyticsEvent);

            const existingUserRedirectUrl =
                await getMethodsAvailableRedirection({
                    email,
                    application: "wac",
                    signupClient,
                    createAnalyticsEvent,
                    successRedirect: location.href
                }).catch((error) => {
                    setFormError(true);
                    setIsSubmitting(false);
                    createAnalyticsEvent({
                        eventName:
                            "gwp.formCloudSignIn.getMethodsAvailableRedirection.submitFailure",
                        eventType: "ui",
                        action: "submitted",
                        actionSubject: "modal",
                        actionSubjectId:
                            "getMethodsAvailableRedirectionFormSubmitFailed",
                        attributes: {
                            event: "submitFailed"
                        }
                    }).fire();
                    throw new Error(error);
                });

            if (existingUserRedirectUrl) {
                window.location.assign(
                    existingUserRedirectUrl.replace("/initiate/password", "")
                );
            } else {
                const isWacIcLoginEnabled = checkFeatureFlagGate(
                    featureFlags.wacIcLogin
                );
                const applicationParam = isWacIcLoginEnabled
                    ? "&application=wac--ic"
                    : "";
                const finalLoginUrl = `${getIdentityBaseUrl()}/login?login_hint=${email}&prompt=none&continue=${
                    location.href
                }${applicationParam}`;
                window.location.assign(finalLoginUrl);
            }

            return;
        },
        [
            createAnalyticsEvent,
            executeRecaptcha,
            isEmailValid,
            recaptchaRetryTimeRemainingSecond,
            signupClient
        ]
    );

    useEffect(() => {
        if (recaptchaFailedToLoad && recaptchaRetryTimeRemainingSecond <= 0) {
            ufoEventAccountSignin.failure({
                customData: {
                    reactComponent: "AccountSigninContextProvider",
                    recaptchaRetryTimeRemainingSecond: 0
                },
                error: new Error("Recaptcha retry failed")
            });
        }
        if (onSubmitProps && recaptchaFailedToLoad && executeRecaptcha) {
            onSubmitWithReCAPTCHA(onSubmitProps)
                .then(() => {
                    ufoEventAccountSignin.success({
                        customData: {
                            reactComponent: "AccountSigninContextProvider",
                            analyticsEvent: "succeeded",
                            recaptchaRetryTimeRemainingSecond
                        }
                    });
                })
                .catch((error) => {
                    setFormError(true);
                    ufoEventAccountSignin.failure({
                        customData: {
                            reactComponent: "AccountSigninContextProvider",
                            analyticsEvent: "errored",
                            recaptchaRetryTimeRemainingSecond
                        },
                        error
                    });
                    return;
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        }
    }, [
        executeRecaptcha,
        recaptchaFailedToLoad,
        onSubmitProps,
        onSubmitWithReCAPTCHA,
        recaptchaRetryTimeRemainingSecond
    ]);

    useEffect(() => {
        if (!recaptchaFailedToLoad) return;
        if (recaptchaRetryTimeRemainingSecond <= 0) return;
        const intervalId = setInterval(() => {
            setRecaptchaRetryTimeRemainingSecond(
                recaptchaRetryTimeRemainingSecond - 1
            );
        }, 1000);
        return () => clearInterval(intervalId);
    }, [recaptchaFailedToLoad, recaptchaRetryTimeRemainingSecond]);

    const resetClickCountLogic = useCallback(() => {
        // Click action on Signin Prompt CTA resets click count logic
        setLocalStorage("general_signin_prompt_x_clicks", "0");
        setLocalStorage(
            "general_signin_prompt_last_visit_timestamp",
            new Date().toISOString()
        );
    }, []);

    const onClickSocialButton = useCallback(
        (provider: string) => {
            trackContinueWithSocialSignin(createAnalyticsEvent, provider);

            const socialRedirectUrl = getSocialButtonUrl({
                redirectTo: location.href,
                identityCobranding: "wac",
                provider
            });

            resetClickCountLogic();
            window.location.assign(socialRedirectUrl);
        },
        [createAnalyticsEvent, location.href, resetClickCountLogic]
    );

    const onClickSubmitButton = useCallback(() => {
        resetClickCountLogic();
        return createAnalyticsEvent({
            eventName: "gwp.accountSignin.onclickSubmit",
            eventType: "ui",
            action: "clicked",
            actionSubject: "linkButton",
            actionSubjectId: "GenearlSigninPromptCtaSubmit"
        }).fire();
    }, [createAnalyticsEvent, resetClickCountLogic]);

    const contextValue = useMemo(
        () => ({
            formError,
            isReadyForInvalid,
            isSubmitting,
            onClickSocialButton,
            onClickSubmitButton,
            onSubmitWithReCAPTCHA,
            redirectTo: location.href,
            setIsEmailValid,
            setIsReadyForInvalid,
            setTypeAheadEmail,
            typeAheadEmail
        }),
        [
            formError,
            isReadyForInvalid,
            isSubmitting,
            onClickSocialButton,
            onClickSubmitButton,
            onSubmitWithReCAPTCHA,
            location.href,
            typeAheadEmail
        ]
    );

    return (
        <AccountSigninContext.Provider value={contextValue}>
            {children}
        </AccountSigninContext.Provider>
    );
};
